export const parseJson = (jsonString) => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return null;
  }
};

export const formatNumberWithMaxDecimals = (number, decimalPoints = 3) => {
  return number.toString().split('.')[1] || ''.length > decimalPoints
    ? '$' + parseFloat(number).toFixed(decimalPoints)
    : number === ' -'
      ? number
      : '$' + number;
};

export const balanceFormatter = (balance, decimalPoints = 2, compactNotation = false) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimalPoints,
    notation: compactNotation ? 'compact' : 'standard',
  });
  if (balance) {
    const result = formatter.format(balance);
    return result;
  } else {
    return '$0.00';
  }
};

export const numberWithComma = (x) => {
  if (x === '' || x === undefined) {
    return x;
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatCount = (value) =>
  Number.isFinite(Number(value)) ? numberWithComma(Number(value)) : value || '-';

export const isSlackWebhook = (url = '') => {
  return url.includes('hooks.slack.com');
};

export const percentager = (value, total) => {
  if (total === 0) {
    return 0;
  }
  return ((value / total) * 100).toFixed(1);
};

export const isNumberString = (str) => {
  return /^\d+$/.test(str);
};

export const transformData = (obj) => {
  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (typeof value === 'string') {
      try {
        obj[key] = JSON.parse(value);
      } catch (e) {
        // keep original value if it's not valid JSON
        obj[key] = value;
      }
    }
  });

  return obj;
};

export const stringLength = (str, len = 30) => {
  if (str?.length > len) {
    return true;
  } else {
    return false;
  }
};

export const getFullName = (first, last) => {
  const firstName = first?.trim() || '';
  const lastName = last?.trim() || '';
  const fullName = firstName || lastName ? `${firstName} ${lastName}`.trim() : ' -';
  return fullName;
};

export const copyToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {})
    .catch((err) => console.error('Failed to copy text: ', err));
};

export const trimName = (name) => {
  const words = name.trim().split(/\s+/);
  return words.length > 1 ? words.join(' ') : words[0];
};

export const getParsedObject = (data) => {
  try {
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (error) {
    return error;
  }
};

export const getEvent = (data) => {
  if (data?.event) {
    return data?.event;
  }

  const requestBody = getParsedObject(data?.value?.request?.body);

  if (isSlackWebhook(data?.value?.request?.url)) {
    const slackText = getParsedObject(requestBody?.text);
    return slackText?.event || ' -';
  }

  return requestBody?.event || ' -';
};

export const trimSpaces = (str) => {
  return str.replace(/\s+/g, ' ').trim();
};

export const removeSThroughOne = (message) => {
  // Replace '1 <word>s' with '1 <word>' for any word followed by 's'
  let result = message.replace(/\b1 (\w+)s\b/g, '1 $1');

  return result;
};

export const downloadFile = async (fileUrl, fileName) => {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const blob = await response.blob();
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('There was a problem with the download:', error);
  }
};

export const validURL = (str) => {
  if (typeof str !== 'string') return false;

  const trimmedValue = str.trim();
  if (!trimmedValue || /\s/.test(trimmedValue)) return false;

  const isValidHostname = (hostname) => {
    if (hostname === 'localhost') return true;

    const ipv4Parts = hostname.split('.');
    if (ipv4Parts.length === 4 && ipv4Parts.every((part) => /^\d+$/.test(part))) {
      return ipv4Parts.every((part) => Number(part) >= 0 && Number(part) <= 255);
    }

    if (!hostname.includes('.')) return false;

    return hostname.split('.').every((part) => (
      part.length > 0 &&
      part.length <= 63 &&
      /^[a-z\d-]+$/i.test(part) &&
      !part.startsWith('-') &&
      !part.endsWith('-')
    ));
  };

  try {
    const hasProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmedValue);
    const valueWithProtocol = hasProtocol ? trimmedValue : `https://${trimmedValue}`;
    const url = new URL(valueWithProtocol);

    return (
      ['http:', 'https:'].includes(url.protocol) &&
      !url.username &&
      !url.password &&
      isValidHostname(url.hostname)
    );
  } catch {
    return false;
  }
};

export const isOfferGeneratorWebsite = () => {
  return window?.location?.hostname?.includes('offer-generator');
};

// Simple debounce function
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

// Get the hamburger class for the header
export const getHamburgerClass = ({ isAdminMode, currentMode, isImpersonatedMode }) => {
  if (isAdminMode) return 'adminHamburger';
  if (currentMode === 'Test') return isImpersonatedMode ? 'hamburgerAdjust' : 'adminHamburger';
  if (isImpersonatedMode && currentMode === 'Live') return 'impersonateHamburger';
  return '';
};

// Truncate string to 20 characters
export const truncateString = (name) => {
  if (name.length > 20) {
    return name.slice(0, 16) + '...';
  }
  return name;
};

export const isProduction = () => {
  return import.meta.env.VITE_APP_ENV === 'production';
};

// Format phone number to (xxx) xxx-xxxx format
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove all non-digits
  const cleaned = phoneNumber.replace(/\D/g, '');

  // If no digits, return empty string
  if (!cleaned) return '';

  // Format based on length
  if (cleaned.length <= 3) {
    return `(${cleaned}`;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else {
    // For numbers longer than 10 digits, only take the first 10
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
};

// Handle phone number input changes with formatting
export const handlePhoneNumberChange = (value, setFieldValue, fieldName) => {
  const formatted = formatPhoneNumber(value);
  setFieldValue(fieldName, formatted);
};

// Origins (comma-separated in VITE_APP_TRUSTED_REDIRECT_ORIGINS) that may receive redirects from OLC
const getTrustedRedirectOrigins = () =>
  (import.meta.env.VITE_APP_TRUSTED_REDIRECT_ORIGINS || '')
    .split(',')
    .map((origin) => origin.trim().replace(/\/$/, ''))
    .filter(Boolean);

// True for http(s) URLs. When VITE_APP_TRUSTED_REDIRECT_ORIGINS is set, the origin must also be
// in that allowlist (prevents open redirects); when it is empty, any http(s) URL is accepted.
export const isTrustedRedirect = (url) => {
  try {
    const parsed = new URL(url);
    if (!['http:', 'https:'].includes(parsed.protocol)) return false;

    const trustedOrigins = getTrustedRedirectOrigins();
    return trustedOrigins.length === 0 || trustedOrigins.includes(parsed.origin);
  } catch (error) {
    return false;
  }
};

// Allows only same-app relative paths like "/campaigns?x=1"; anything else falls back to "/"
export const sanitizeInternalPath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return '/';
  if (path.includes('\\') || /[\u0000-\u001f]/.test(path)) return '/';
  return path;
};

// Remembers where to land after the 2FA prompt that follows a handoff login (survives only this tab)
const POST_LOGIN_PATH_KEY = 'postLoginPath';

export const setPostLoginPath = (path) => {
  try {
    sessionStorage.setItem(POST_LOGIN_PATH_KEY, sanitizeInternalPath(path));
  } catch (error) {
    // sessionStorage unavailable: fall back to the default landing page
  }
};

// Returns the remembered path (once) or "/" when there is none
export const consumePostLoginPath = () => {
  try {
    const path = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
    sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
    return sanitizeInternalPath(path);
  } catch (error) {
    return '/';
  }
};
